import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Pagination,
  CircularProgress,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Container,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import axios from '../../../services/axios';
import Header from '../../../layout/Header';
import FooterComponent from "../../../layout/Footer";

// 🟩 Card uniforme en alto y ancho
const CustomCard = styled(Card)(({ theme }) => ({
  width: 250, // ancho fijo
  height: 400, // alto fijo
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0px 6px 16px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  background: 'linear-gradient(145deg, #ffffff, #f9f9f9)',
  margin: 'auto',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0px 12px 24px rgba(0,0,0,0.2)',
  },
}));

// 🟦 Imagen ajustada sin deformar
const CustomCardMedia = styled(CardMedia)({
  width: '100%',
  height: 180,
  objectFit: 'cover',
  objectPosition: 'center',
  transition: 'transform 0.4s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

// 🟨 Contenido centrado
const CustomCardContent = styled(CardContent)({
  flexGrow: 1,
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: 120,
});

const CustomCardActions = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '12px',
});

const CustomButton = styled(Button)(({ theme }) => ({
  borderRadius: 30,
  padding: '8px 20px',
  textTransform: 'none',
  fontWeight: 'bold',
  background: 'linear-gradient(45deg, #ff4b2b, #ff416c)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #e04324, #d9365f)',
  },
}));

const CustomCardSection = styled(Box)({
  marginTop: '0.5rem',
  width: '100%',
});

const DestinosList = () => {
  const [destinos, setDestinos] = useState([]);
  const [itemsPorPagina] = useState(8);
  const [paginaActual, setPaginaActual] = useState(1);
  const [categoria, setCategoria] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDestinos = async () => {
      try {
        const response = await axios.get('/destinos');
        setDestinos(
          response.data.filter(
            (destino) => destino.categoria === 'Hoteles'
          )
        );
      } catch (error) {
        console.error('Error al obtener los destinos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinos();
  }, []);

  const handlePageChange = (event, value) => {
    setPaginaActual(value);
  };

  const handleCategoriaChange = (event) => {
    setCategoria(event.target.value);
    setPaginaActual(1);
  };

  const destinosFiltrados = categoria
    ? destinos.filter((destino) => destino.ciudad === categoria)
    : destinos;

  const totalPaginas = Math.ceil(destinosFiltrados.length / itemsPorPagina);

  const paginatedDestinos = () => {
    const inicio = (paginaActual - 1) * itemsPorPagina;
    const fin = inicio + itemsPorPagina;
    return destinosFiltrados.slice(inicio, fin);
  };

  const ciudadesUnicas = [...new Set(destinos.map((destino) => destino.ciudad))].sort();

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <Header />

      <Box component="main" flexGrow={1}>
        <Container sx={{ mt: 4, mb: 4 }}>
          {/* Filtro de ciudades */}
          <Box display="flex" justifyContent="center" mb={4}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 250 } }}>
              <InputLabel id="select-categoria-label">Seleccionar Ciudad</InputLabel>
              <Select
                labelId="select-categoria-label"
                id="select-categoria"
                value={categoria}
                label="Seleccionar Ciudad"
                onChange={handleCategoriaChange}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {ciudadesUnicas.map((ciudad, index) => (
                  <MenuItem key={index} value={ciudad}>
                    {ciudad}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Lista de destinos */}
          <Box display="flex" justifyContent="center">
            {loading ? (
              <CircularProgress />
            ) : (
              <Grid container spacing={3} justifyContent="center">
                {paginatedDestinos().map((destino) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={destino.id}>
                    <CustomCard>
                      <CustomCardMedia
                        component="img"
                        alt={destino.nombre}
                        image={destino.foto}
                      />
                      <CustomCardContent>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                            fontWeight: 'bold',
                            color: '#333',
                            lineHeight: 1.2,
                            mb: 1,
                          }}
                        >
                          {destino.nombre}
                        </Typography>

                        <Divider sx={{ my: 1, width: '60%' }} />

                        <CustomCardSection>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Especialidad:</strong>{' '}
                            {destino.subcategoria || 'No especificado'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Ciudad:</strong> {destino.ciudad}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Dirección:</strong> {destino.direccion}
                          </Typography>
                        </CustomCardSection>
                      </CustomCardContent>

                      <CustomCardActions>
                        <CustomButton
                          component={Link}
                          to={`/informacion/${destino.id}`}
                          fullWidth
                        >
                          Ver más
                        </CustomButton>
                      </CustomCardActions>
                    </CustomCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {/* Paginación */}
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPaginas}
              page={paginaActual}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        </Container>
      </Box>
      <FooterComponent/>
    </Box>
  );
};

export default DestinosList;
